import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  getDocs, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  serverTimestamp, 
  orderBy 
} from 'firebase/firestore';
import { db } from '../../firebase';
import Select from 'react-select';

const ResearchManagement = () => {
  const [researchPosts, setResearchPosts] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [currentPost, setCurrentPost] = useState(null);
  const [showTagForm, setShowTagForm] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 5;

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    authors: [],
    institution: '',
    abstract: '',
    externalLink: '',
    researchGateProfile: '',
    tags: [],
    publishedDate: ''
  });

  // Load research posts and tags
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch tags
        const tagsQuery = query(collection(db, "research_tags"), orderBy("name"));
        const tagsSnapshot = await getDocs(tagsQuery);
        const tagsList = tagsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setTags(tagsList);

        // Fetch research posts
        const postsQuery = query(collection(db, "research_posts"), orderBy("publishedDate", "desc"));
        const postsSnapshot = await getDocs(postsQuery);
        const postsList = postsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setResearchPosts(postsList);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
      setLoading(false);
    };

    fetchData();
  }, []);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle authors input (comma-separated)
  const handleAuthorsChange = (e) => {
    const authors = e.target.value.split(',').map(author => author.trim()).filter(author => author);
    setFormData(prev => ({ ...prev, authors }));
  };

  // Handle tags selection
  const handleTagChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    setFormData(prev => ({ ...prev, tags: selectedOptions }));
  };

  // Add new tag
  const handleAddTag = async (e) => {
    e.preventDefault();
    if (!newTag.trim()) return;

    try {
      const tagRef = await addDoc(collection(db, "research_tags"), {
        name: newTag.trim(),
        createdAt: serverTimestamp()
      });
      
      setTags(prev => [...prev, { id: tagRef.id, name: newTag.trim() }]);
      setNewTag('');
      setShowTagForm(false);
    } catch (error) {
      console.error("Error adding tag:", error);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      title: '',
      authors: [],
      institution: '',
      abstract: '',
      externalLink: '',
      researchGateProfile: '',
      tags: [],
      publishedDate: ''
    });
    setCurrentPost(null);
    setEditMode(false);
  };

  // Edit research post
  const handleEdit = (post) => {
    setCurrentPost(post);
    setFormData({
      title: post.title || '',
      authors: post.authors || [],
      institution: post.institution || '',
      abstract: post.abstract || '',
      externalLink: post.externalLink || '',
      researchGateProfile: post.researchGateProfile || '',
      tags: post.tags || [],
      publishedDate: post.publishedDate?.toDate?.() 
        ? new Date(post.publishedDate.toDate()).toISOString().split('T')[0] 
        : ''
    });
    setEditMode(true);
  };

  // Delete research post
  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this research post?')) return;

    try {
      await deleteDoc(doc(db, "research_posts", postId));
      setResearchPosts(prev => prev.filter(post => post.id !== postId));
    } catch (error) {
      console.error("Error deleting post:", error);
    }
  };

  // Submit form (create or update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Convert date string to timestamp or use current date
      const publishedDate = formData.publishedDate 
        ? new Date(formData.publishedDate) 
        : new Date();
      
      // Prepare post data
      const postData = {
        title: formData.title,
        authors: formData.authors,
        institution: formData.institution,
        abstract: formData.abstract,
        externalLink: formData.externalLink,
        researchGateProfile: formData.researchGateProfile,
        tags: formData.tags,
        publishedDate: publishedDate,
        updatedAt: serverTimestamp()
      };
      
      if (editMode && currentPost) {
        // Update existing post
        await updateDoc(doc(db, "research_posts", currentPost.id), postData);
        
        // Update local state
        setResearchPosts(prev => prev.map(post => 
          post.id === currentPost.id 
            ? { ...post, ...postData, id: currentPost.id } 
            : post
        ));
      } else {
        // Create new post
        postData.createdAt = serverTimestamp();
        
        const docRef = await addDoc(collection(db, "research_posts"), postData);
        
        // Update local state
        setResearchPosts(prev => [
          { ...postData, id: docRef.id },
          ...prev
        ]);
      }
      
      // Reset form
      resetForm();
    } catch (error) {
      console.error("Error saving post:", error);
    }
  };

  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Pagination logic
  const totalPages = Math.ceil(researchPosts.length / postsPerPage);
  const paginatedPosts = researchPosts.slice(
    (currentPage - 1) * postsPerPage,
    currentPage * postsPerPage
  );

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // React-Select options for tags
  const tagOptions = tags.map(tag => ({ value: tag.id, label: tag.name }));

  // Handle tag selection with react-select
  const handleTagSelect = (selectedOptions) => {
    setFormData(prev => ({
      ...prev,
      tags: selectedOptions ? selectedOptions.map(option => option.value) : []
    }));
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Research Management</h1>
      
      {/* Research Post Form */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-8">
        <h2 className="text-xl font-semibold mb-4">{editMode ? 'Edit Research Post' : 'Add New Research Post'}</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
              required
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Authors (comma-separated)</label>
              <input
                type="text"
                name="authors"
                value={formData.authors.join(', ')}
                onChange={handleAuthorsChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Institution</label>
              <input
                type="text"
                name="institution"
                value={formData.institution}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Abstract</label>
            <textarea
              name="abstract"
              value={formData.abstract}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
              required
            ></textarea>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">External Link</label>
              <input
                type="url"
                name="externalLink"
                value={formData.externalLink}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
                placeholder="https://example.com/paper"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ResearchGate Profile</label>
              <input
                type="url"
                name="researchGateProfile"
                value={formData.researchGateProfile}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
                placeholder="https://researchgate.net/profile/..."
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tags/Fields of Study
                <button 
                  type="button"
                  onClick={() => setShowTagForm(!showTagForm)}
                  className="ml-2 text-[rgb(130,88,18)] text-xs underline"
                >
                  {showTagForm ? 'Cancel' : 'Add new tag'}
                </button>
              </label>
              
              {showTagForm ? (
                <div className="flex">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
                    placeholder="New tag name"
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="px-4 py-2 bg-[rgb(130,88,18)] text-white rounded-r-md hover:bg-[rgb(110,68,0)]"
                  >
                    Add
                  </button>
                </div>
              ) : (
                <Select
                  isMulti
                  name="tags"
                  options={tagOptions}
                  value={tagOptions.filter(option => formData.tags.includes(option.value))}
                  onChange={handleTagSelect}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder="Select tags..."
                />
              )}
              <p className="text-xs text-gray-500 mt-1">Search and select multiple tags</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Publication Date</label>
              <input
                type="date"
                name="publishedDate"
                value={formData.publishedDate}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
              />
            </div>
          </div>
          
          <div className="flex justify-end space-x-4">
            {editMode && (
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="px-4 py-2 bg-[rgb(130,88,18)] text-white rounded-md hover:bg-[rgb(110,68,0)]"
            >
              {editMode ? 'Update Post' : 'Add Post'}
            </button>
          </div>
        </form>
      </div>
      
      {/* Research Posts List */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold mb-4">Research Posts</h2>
        
        {loading ? (
          <div className="flex justify-center my-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[rgb(130,88,18)]"></div>
          </div>
        ) : researchPosts.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Authors</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Published</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tags</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedPosts.map(post => (
                    <tr key={post.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{post.title}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500">{post.authors?.join(', ') || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{formatDate(post.publishedDate)}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {post.tags?.map((tagId, index) => {
                            const tag = tags.find(t => t.id === tagId);
                            return tag ? (
                              <span key={index} className="px-2 py-1 bg-gray-100 text-xs rounded-full">
                                {tag.name}
                              </span>
                            ) : null;
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleEdit(post)}
                          className="text-indigo-600 hover:text-indigo-900 mr-4"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(post.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination Controls */}
            <div className="flex items-center justify-between mt-6">
              <div>
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <input
                  type="number"
                  min={1}
                  max={totalPages}
                  value={currentPage}
                  onChange={e => handlePageChange(Number(e.target.value))}
                  className="w-16 px-2 py-1 border rounded text-center"
                />
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        ) : (
          <p className="text-center py-4 text-gray-500">No research posts found. Add your first one above!</p>
        )}
      </div>
    </div>
  );
};

export default ResearchManagement; 